export { toDocumentExtensionContext } from './document-extension-context';

export interface DocumentFileNameParts {
    readonly title: string;
    readonly badge: DocumentExtensionBadge | null;
}

export interface DocumentExtensionBadge {
    readonly label: string;
}

export function isDwgDocumentFileName(fileName: string): boolean {
    return fileName.toLowerCase().endsWith('.dwg');
}

export function toDocumentDownloadActionLabel(fileName: string): string {
    return isDwgDocumentFileName(fileName)
        ? 'Download original DWG'
        : 'Download';
}

export function toDocumentDownloadAriaLabel(fileName: string): string {
    return isDwgDocumentFileName(fileName)
        ? `Download original DWG ${fileName}`
        : `Download ${fileName}`;
}

export function toDocumentPreviewStatusLabel(
    fileName: string,
    previewStatus: string | null | undefined,
    previewAvailable: boolean | undefined
): string | null {
    if (!isDwgDocumentFileName(fileName)) return null;
    if (previewAvailable) return 'PDF preview ready';

    const normalizedStatus = previewStatus?.toLowerCase();
    if (
        normalizedStatus === 'pending' ||
        normalizedStatus === 'queued' ||
        normalizedStatus === 'processing'
    ) {
        return 'Generating PDF preview';
    }

    if (normalizedStatus === 'failed' || normalizedStatus === 'unavailable') {
        return 'PDF preview unavailable';
    }

    return 'PDF preview not available';
}

export function toDocumentFileNameParts(
    fileName: string
): DocumentFileNameParts {
    const match = /^(?<title>.+)\.(?<extension>[^.]+)$/.exec(fileName);
    if (!match?.groups) return { title: fileName, badge: null };

    const extension = match.groups['extension'];
    const label = toDocumentExtensionLabel(extension);

    return {
        title: match.groups['title'],
        badge: {
            label,
        },
    };
}

export function toDocumentExtensionLabel(extension: string): string {
    return /^[a-z0-9]+$/i.test(extension) ? extension.toUpperCase() : extension;
}
